import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconUserPenFill18 as AssigneeIcon,
	IconArrowsRotateCenterFill18 as OriginIconCat,
	IconCircleDashedFill18 as StatusIcon,
	IconTagsFill18 as TagIcon,
} from "nucleo-ui-fill-18"
import {
	IconArrowDownOutline18 as ArrowDown,
	IconArrowUpOutline18 as ArrowUp,
	IconCheckOutline18 as Check,
	IconCheckDoubleOutline18 as DoneIcon,
	IconInboxOutline18 as OpenIcon,
	IconClockOutline18 as SnoozedIcon,
} from "nucleo-ui-outline-18"

import type { FeedbackOrigin } from "@productlane/db/browser"
import type { IconType } from "@productlane/ui"
import type { TeamMember } from "@productlane/zero/queries"
import { FeedbackOriginTextRecord } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { Avatar, IconButton, Tooltip } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { FilterChip, SearchableFilterItem } from "@/components/filters"
import {
	FilterControlChips,
	FilterPopover,
	useFilteredSearchItems,
} from "@/components/filters"
import { Tag } from "@/components/tag"
import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { useAuthData } from "@/lib/zero"

export type SortOrder = "asc" | "desc"

export type StatusGroup = "open" | "snoozed" | "done"

export interface DetailThreadsFilters {
	statuses: StatusGroup[]
	origins: FeedbackOrigin[]
	assigneeIds: (string | null)[]
	tagIds: string[]
}

export const EMPTY_DETAIL_FILTERS: DetailThreadsFilters = {
	statuses: [],
	origins: [],
	assigneeIds: [],
	tagIds: [],
}

type Cat = "Status" | "Assignee" | "Origin" | "Tag"

const CATEGORY_ORDER: Cat[] = ["Status", "Assignee", "Origin", "Tag"]

const CAT_LABEL: Record<Cat, string> = {
	Status: "Status",
	Assignee: "Assignee",
	Origin: "Origin",
	Tag: "Tag",
}

const CAT_ICON: Record<Cat, IconType> = {
	Status: StatusIcon,
	Assignee: AssigneeIcon,
	Origin: OriginIconCat,
	Tag: TagIcon,
}

const STATUS_OPTIONS: { value: StatusGroup; label: string; Icon: IconType }[] =
	[
		{ value: "open", label: "Open", Icon: OpenIcon },
		{ value: "snoozed", label: "Snoozed", Icon: SnoozedIcon },
		{ value: "done", label: "Done", Icon: DoneIcon },
	]

interface OriginEntry {
	key: string
	label: string
	Icon: IconType
	origins: FeedbackOrigin[]
}

const GROUPED_ORIGINS: OriginEntry[] = [
	{
		key: "slack",
		label: "Slack",
		Icon: FeedbackOriginIconRecord.SLACK,
		origins: ["SLACK", "SLACK_CONNECT"],
	},
	{
		key: "intercom",
		label: "Intercom",
		Icon: FeedbackOriginIconRecord.INTERCOM,
		origins: ["INTERCOM", "INTERCOM_ATTACHMENT"],
	},
	{
		key: "zendesk",
		label: "Zendesk",
		Icon: FeedbackOriginIconRecord.ZENDESK,
		origins: ["ZENDESK", "ZENDESK_ATTACHMENT"],
	},
]

const INDIVIDUAL_ORIGINS: FeedbackOrigin[] = [
	"EMAIL",
	"PORTAL",
	"SUPPORT_PORTAL",
	"WIDGET",
	"AI_CHAT",
	"LIVE_CHAT",
	"INAPP",
	"LINEAR",
	"API",
	"CUSTOM",
	"TEAMS",
	"ZAPIER",
	"HUBSPOT",
	"CALENDAR",
]

const ORIGIN_ENTRIES: OriginEntry[] = [
	...GROUPED_ORIGINS,
	...INDIVIDUAL_ORIGINS.map((origin) => ({
		key: origin,
		label: FeedbackOriginTextRecord[origin].replace("via ", ""),
		Icon: FeedbackOriginIconRecord[origin],
		origins: [origin],
	})),
]

function toggle<T>(list: T[], value: T): T[] {
	return list.includes(value)
		? list.filter((v) => v !== value)
		: [...list, value]
}

function toggleOriginGroup(filters: DetailThreadsFilters, entry: OriginEntry) {
	const selected = entry.origins.some((o) => filters.origins.includes(o))
	return {
		...filters,
		origins: selected
			? filters.origins.filter((o) => !entry.origins.includes(o))
			: [...filters.origins, ...entry.origins],
	}
}

function originGroupCount(filters: DetailThreadsFilters) {
	const groups = new Set<string>()
	for (const o of filters.origins) {
		const group = ORIGIN_ENTRIES.find((g) => g.origins.includes(o))
		groups.add(group ? group.key : o)
	}
	return groups.size
}

function useTags() {
	const decodedToken = useAuthData()
	const [tagsRaw] = useQuery(queries.tags(decodedToken))
	return useMemo(() => {
		const seen = new Set<string>()
		return tagsRaw.filter((t) => {
			if (seen.has(t.id)) return false
			seen.add(t.id)
			return true
		})
	}, [tagsRaw])
}

interface CategoryPanelProps {
	cat: Cat
	filters: DetailThreadsFilters
	onChange: (next: DetailThreadsFilters) => void
	teamMembers: readonly TeamMember[]
	tags: ReturnType<typeof useTags>
	onAfterSelect?: () => void
}

function CategoryPanel({
	cat,
	filters,
	onChange,
	teamMembers,
	tags,
	onAfterSelect,
}: CategoryPanelProps) {
	const [query, setQuery] = useState("")

	const checkClass = (selected: boolean) =>
		cn("h-4 w-4 shrink-0", { "opacity-100": selected, "opacity-0": !selected })

	return (
		<Command>
			<CommandInput
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{cat === "Status" &&
						STATUS_OPTIONS.map((option) => {
							const selected = filters.statuses.includes(option.value)
							return (
								<CommandItem
									key={option.value}
									value={option.label}
									onSelect={() => {
										onChange({
											...filters,
											statuses: toggle(filters.statuses, option.value),
										})
										onAfterSelect?.()
									}}
								>
									<div className="flex w-full items-center gap-2">
										<option.Icon className="h-4 w-4 shrink-0 text-secondary" />
										<span className="flex-1 truncate">{option.label}</span>
										<Check className={checkClass(selected)} />
									</div>
								</CommandItem>
							)
						})}

					{cat === "Assignee" && (
						<>
							<CommandItem
								value="Unassigned"
								onSelect={() => {
									onChange({
										...filters,
										assigneeIds: toggle(filters.assigneeIds, null),
									})
									onAfterSelect?.()
								}}
							>
								<div className="flex w-full items-center gap-2">
									<Avatar name="No assignee" showAvatarFallback size="xs" />
									<span className="flex-1 truncate">Unassigned</span>
									<Check
										className={checkClass(filters.assigneeIds.includes(null))}
									/>
								</div>
							</CommandItem>
							{teamMembers.map((member) => {
								const label = member.user?.name || member.user?.email || ""
								const selected = filters.assigneeIds.includes(member.userId)
								return (
									<CommandItem
										key={member.id}
										value={label}
										onSelect={() => {
											onChange({
												...filters,
												assigneeIds: toggle(filters.assigneeIds, member.userId),
											})
											onAfterSelect?.()
										}}
									>
										<div className="flex w-full items-center gap-2">
											<Avatar
												image={member.user?.imageUrl}
												name={label}
												size="xs"
											/>
											<span className="flex-1 truncate">{label}</span>
											<Check className={checkClass(selected)} />
										</div>
									</CommandItem>
								)
							})}
						</>
					)}

					{cat === "Origin" &&
						ORIGIN_ENTRIES.map((entry) => {
							const selected = entry.origins.some((o) =>
								filters.origins.includes(o),
							)
							return (
								<CommandItem
									key={entry.key}
									value={entry.label}
									onSelect={() => {
										onChange(toggleOriginGroup(filters, entry))
										onAfterSelect?.()
									}}
								>
									<div className="flex w-full items-center gap-2">
										<entry.Icon className="h-4 w-4 shrink-0 text-secondary" />
										<span className="flex-1 truncate">{entry.label}</span>
										<Check className={checkClass(selected)} />
									</div>
								</CommandItem>
							)
						})}

					{cat === "Tag" &&
						tags.map((tag) => {
							const selected = filters.tagIds.includes(tag.id)
							return (
								<CommandItem
									key={tag.id}
									value={tag.name}
									onSelect={() => {
										onChange({
											...filters,
											tagIds: toggle(filters.tagIds, tag.id),
										})
										onAfterSelect?.()
									}}
								>
									<div className="flex w-full items-center gap-2">
										<Tag tag={tag} />
										<span className="flex-1" />
										<Check className={checkClass(selected)} />
									</div>
								</CommandItem>
							)
						})}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function DetailThreadsToolbar({
	sortOrder,
	onSortOrderChange,
	filters,
	onFiltersChange,
	teamMembers,
}: {
	sortOrder: SortOrder
	onSortOrderChange: (order: SortOrder) => void
	filters: DetailThreadsFilters
	onFiltersChange: (filters: DetailThreadsFilters) => void
	teamMembers: readonly TeamMember[]
}) {
	const tags = useTags()
	const [globalQuery, setGlobalQuery] = useState("")

	const categories = useMemo(
		() =>
			CATEGORY_ORDER.map((type) => ({
				type,
				label: CAT_LABEL[type],
				Icon: CAT_ICON[type],
			})),
		[],
	)

	const counts = useMemo<Record<Cat, number>>(
		() => ({
			Status: filters.statuses.length,
			Assignee: filters.assigneeIds.length,
			Origin: originGroupCount(filters),
			Tag: filters.tagIds.length,
		}),
		[filters],
	)

	const searchableItems = useMemo<SearchableFilterItem<Cat>[]>(() => {
		const items: SearchableFilterItem<Cat>[] = []

		for (const option of STATUS_OPTIONS) {
			items.push({
				id: `Status-${option.value}`,
				category: "Status",
				categoryLabel: "Status",
				label: option.label,
				searchText: option.label,
				Icon: option.Icon,
				isSelected: filters.statuses.includes(option.value),
				onSelect: () =>
					onFiltersChange({
						...filters,
						statuses: toggle(filters.statuses, option.value),
					}),
			})
		}

		items.push({
			id: "Assignee-none",
			category: "Assignee",
			categoryLabel: "Assignee",
			label: "Unassigned",
			searchText: "Unassigned",
			isSelected: filters.assigneeIds.includes(null),
			onSelect: () =>
				onFiltersChange({
					...filters,
					assigneeIds: toggle(filters.assigneeIds, null),
				}),
		})
		for (const member of teamMembers) {
			const label = member.user?.name || member.user?.email || ""
			items.push({
				id: `Assignee-${member.id}`,
				category: "Assignee",
				categoryLabel: "Assignee",
				label,
				searchText: label,
				renderCustom: () => (
					<Avatar image={member.user?.imageUrl} name={label} size="xs" />
				),
				isSelected: filters.assigneeIds.includes(member.userId),
				onSelect: () =>
					onFiltersChange({
						...filters,
						assigneeIds: toggle(filters.assigneeIds, member.userId),
					}),
			})
		}

		for (const entry of ORIGIN_ENTRIES) {
			items.push({
				id: `Origin-${entry.key}`,
				category: "Origin",
				categoryLabel: "Origin",
				label: entry.label,
				searchText: entry.label,
				Icon: entry.Icon,
				isSelected: entry.origins.some((o) => filters.origins.includes(o)),
				onSelect: () => onFiltersChange(toggleOriginGroup(filters, entry)),
			})
		}

		for (const tag of tags) {
			items.push({
				id: `Tag-${tag.id}`,
				category: "Tag",
				categoryLabel: "Tag",
				label: tag.name,
				searchText: tag.name,
				iconColor: tag.color,
				isSelected: filters.tagIds.includes(tag.id),
				onSelect: () =>
					onFiltersChange({
						...filters,
						tagIds: toggle(filters.tagIds, tag.id),
					}),
			})
		}

		return items
	}, [filters, teamMembers, tags, onFiltersChange])

	const filteredSearchItems = useFilteredSearchItems(
		globalQuery,
		searchableItems,
	)

	return (
		<div className="flex items-center gap-1">
			<Tooltip label={sortOrder === "desc" ? "Newest first" : "Oldest first"}>
				<IconButton
					variant="ghost"
					className="text-tertiary"
					Icon={sortOrder === "desc" ? ArrowDown : ArrowUp}
					onClick={() =>
						onSortOrderChange(sortOrder === "desc" ? "asc" : "desc")
					}
				/>
			</Tooltip>

			<FilterPopover<Cat>
				categories={categories}
				counts={counts}
				renderSubPanel={(group, _q, _setQ, onFilterItemSelect) => (
					<CategoryPanel
						cat={group}
						filters={filters}
						onChange={onFiltersChange}
						teamMembers={teamMembers}
						tags={tags}
						onAfterSelect={onFilterItemSelect}
					/>
				)}
				searchableItems={searchableItems}
				filteredSearchItems={filteredSearchItems}
				onGlobalQueryChange={setGlobalQuery}
				tooltipLabel="Filter threads"
			/>
		</div>
	)
}

export function DetailThreadsFilterChips({
	filters,
	onChange,
	teamMembers,
}: {
	filters: DetailThreadsFilters
	onChange: (next: DetailThreadsFilters) => void
	teamMembers: readonly TeamMember[]
}) {
	const tags = useTags()

	const chips = useMemo<FilterChip[]>(() => {
		const result: FilterChip[] = []

		const panel = (cat: Cat) => (
			<CategoryPanel
				cat={cat}
				filters={filters}
				onChange={onChange}
				teamMembers={teamMembers}
				tags={tags}
			/>
		)

		if (filters.statuses.length > 0) {
			result.push({
				key: "Status",
				label: "Status",
				middleText: filters.statuses.length > 1 ? "are" : "is",
				valueText:
					filters.statuses.length === 1
						? (STATUS_OPTIONS.find((o) => o.value === filters.statuses[0])
								?.label ?? "")
						: `${filters.statuses.length} selected`,
				onClear: () => onChange({ ...filters, statuses: [] }),
				popoverContent: panel("Status"),
			})
		}

		if (filters.assigneeIds.length > 0) {
			const first = filters.assigneeIds[0]
			const firstLabel =
				first === null
					? "Unassigned"
					: (teamMembers.find((m) => m.userId === first)?.user?.name ??
						teamMembers.find((m) => m.userId === first)?.user?.email ??
						"")
			result.push({
				key: "Assignee",
				label: "Assignee",
				middleText: filters.assigneeIds.length > 1 ? "are" : "is",
				valueText:
					filters.assigneeIds.length === 1
						? firstLabel
						: `${filters.assigneeIds.length} selected`,
				onClear: () => onChange({ ...filters, assigneeIds: [] }),
				popoverContent: panel("Assignee"),
			})
		}

		const originGroups = originGroupCount(filters)
		if (originGroups > 0) {
			const firstEntry = ORIGIN_ENTRIES.find((e) =>
				e.origins.some((o) => filters.origins.includes(o)),
			)
			result.push({
				key: "Origin",
				label: "Origin",
				middleText: originGroups > 1 ? "are" : "is",
				valueText:
					originGroups === 1
						? (firstEntry?.label ?? "")
						: `${originGroups} selected`,
				onClear: () => onChange({ ...filters, origins: [] }),
				popoverContent: panel("Origin"),
			})
		}

		if (filters.tagIds.length > 0) {
			result.push({
				key: "Tag",
				label: "Tag",
				middleText: filters.tagIds.length > 1 ? "are" : "is",
				valueText:
					filters.tagIds.length === 1
						? (tags.find((t) => t.id === filters.tagIds[0])?.name ?? "")
						: `${filters.tagIds.length} selected`,
				onClear: () => onChange({ ...filters, tagIds: [] }),
				popoverContent: panel("Tag"),
			})
		}

		return result
	}, [filters, teamMembers, tags, onChange])

	if (chips.length === 0) return null

	return (
		<FilterControlChips
			chips={chips}
			containerClassName="flex flex-wrap items-center gap-2"
		/>
	)
}
